import { FileRow, IconButton, Label, Tooltip } from '@civilgrid/ui/components';
import { useFeatureFlagEnabled } from '@posthog/react';
import { getCustomDataSourceDownload, type ProjectCustomData } from '~/api/projectCustomData';
import { Notify } from '~/components2/Notify';

export const CUSTOM_DATA_DOWNLOAD_FLAG = 'project-enable-custom-data-download';

async function downloadCustomDataSource(projectId: string, id: string): Promise<void> {
  try {
    const { url } = await getCustomDataSourceDownload(projectId, id);
    // The link is signed as an attachment, so same-tab navigation downloads without leaving the page.
    window.location.href = url;
  } catch {
    Notify.error({ message: "Couldn't download the source file." });
  }
}

function useDownloadableSourceFilename(row: ProjectCustomData): string | null {
  const isEnabled = useFeatureFlagEnabled(CUSTOM_DATA_DOWNLOAD_FLAG) === true;
  return isEnabled ? row.sourceFilename : null;
}

export function CustomDataSourceDownloadButton({ projectId, row }: { projectId: string; row: ProjectCustomData }) {
  if (!useDownloadableSourceFilename(row)) return null;

  return (
    <Tooltip asChild text="Download source zip">
      <IconButton
        icon="download"
        variant="secondary"
        aria-label={`Download source zip for ${row.label}`}
        data-testid={`custom-data-download-${row.id}`}
        onClick={() => downloadCustomDataSource(projectId, row.id)}
      />
    </Tooltip>
  );
}

export function CustomDataSourceFileRow({ projectId, row }: { projectId: string; row: ProjectCustomData }) {
  const sourceFilename = useDownloadableSourceFilename(row);
  if (!sourceFilename) return null;

  return (
    <div className="flex flex-col gap-1">
      <Label>Source file</Label>
      <FileRow
        name={sourceFilename}
        action={
          <IconButton
            icon="download"
            variant="secondary"
            size="small"
            data-testid={`custom-data-edit-download-${row.id}`}
            onClick={() => downloadCustomDataSource(projectId, row.id)}
          >
            Download
          </IconButton>
        }
      />
    </div>
  );
}
